import {useQuery} from '@tanstack/react-query'
import {usePortal} from '../portal'
import {z} from 'zod'
import {ProfileUI} from 'components/portal/profileTypes'
import {P, match} from 'ts-pattern'
import {zQuestionStatus, Forecast as F, type QuestionCfg} from 'shared/forecast'
import {zx, Prettify} from 'shared/type'
import {safeParseList, NotNull, notNull} from 'shared/type'
import {mapEntries, compactObj, keyBy, entries} from 'shared/collection'
import {fmtDate, fmtDuration} from 'shared/format'
import {StyleProps} from 'shared/ui/uiType'
import {MasonryGrid, LabelForm, Expandable, LabelEmptyList} from 'shared/ui'
import {questionStatusMd} from 'shared/forecast/questionStatus'
import {Alert, useSafeMantineTheme, Button, Skeleton, Chip, Badge} from '@mantine/core'
import {useMemo, useState, memo, useCallback, createContext, useContext, CSSProperties} from 'react'
import {groupBy, isEmpty, mapValues, pipe, sortBy, values, unique, range, isNonNullish, pick} from 'remeda'
import {ParticipationStatusIcon, TaskedProgressText} from 'components/question/Participation'
import Link from 'next/link'
import {QuestionTileItems} from 'components/question/QuestionTile'
import {DateInput} from '@mantine/dates'
import {QuestionStatusFilter} from 'components/question/QuestionStatus'
import {useQueryString} from 'utils/useQueryString'
import {DateTime as D, DateTime} from 'luxon'
import {DueLabel} from 'components/question/Due'
import {useQuerySbSync} from 'utils/db/useQuerySb'
import {RichText} from 'components/form/RichText'
import {MdEdit} from 'react-icons/md'
import {showError} from 'components/notify'
import {HiCheck, HiChevronDoubleDown, HiChevronDoubleUp, HiChevronLeft, HiChevronRight} from 'react-icons/hi'
import {useDbForm} from 'components/hooks/useDbForm'
import {ItemMd, ComboMulti} from 'components/form/ComboMulti'
import {useForecasts} from 'components/question/useForecasts'
import {useQuestionQuery, useQuestionsPageQuery} from 'components/question/useQuestions'
import {useSubqQuery} from 'components/question/subq'
import {ForecastStatus} from 'components/question/Forecast'
import {attachmentItems, QuestionAttachmentList} from 'components/question/QuestionAttachment'
import {QuestionSelect, useQuestionSelect} from 'components/question/QuestionSelect'
import {useLatest} from 'components/hooks'
import {SaveStatusCircle} from 'components/form/SaveStatusCircle'
import {sb} from 'utils/db'
import {ProfileFilter} from '../Profile'
import {SAvatar} from '../SAvatar'
import {questionSortTimestamp} from 'shared/forecast/questionOrder'

const zTags = z.array(z.enum(['paid', 'paid-partial', 'pro-bono', 'bonus', 'invoice-received']))
const zQuery = z.object({
  profiles: z.array(z.string()).optional(),
  status: z.array(zQuestionStatus).optional(),
  since: z.string().optional(),
  tags: zTags.optional(),
  questions: z.array(z.string()).optional(),
  showForecasterForecasts: z.array(z.string()).optional(),
  showMore: z.array(z.string()).optional()
})

const zForecastAdminCfg = z.object({
  note: z.string().default(''),
  tags: zTags.default([])
})
type ForecastAdminCfg = z.infer<typeof zForecastAdminCfg>
type AdminStatus = ForecastAdminCfg['tags'][number]

type PartKeyValue = {id: string; profile_id: string}
const partKey = ({id, profile_id}: PartKeyValue) => `${id}:${profile_id}`

const useForecasterParticipation = ({mine, question_id}: {mine?: boolean; question_id?: string}) => {
  const {profile, profilesUI} = usePortal()
  if (!profile) throw new Error('profile required for participation page')
  const {workspace_id} = profile
  const profile_id = mine ? profile.id : undefined

  const {query, setQuery: setFilter} = useQueryString(zQuery)
  const filter = {
    profiles: [],
    status: [],
    since: undefined,
    tags: [],
    questions: [],
    ...query
  }
  const {status, since, tags, showForecasterForecasts} = filter

  const {
    data: callsRaw,
    error,
    isLoading: partLoading,
    refetch: partsRefetch
  } = useQuery({
    queryKey: ['ForecasterParticipation', profile_id, workspace_id, question_id],
    queryFn: async () =>
      await sb
        .from('forecaster_participation')
        .select('*')
        .match(compactObj({profile_id, workspace_id, question_id}))
        .then(r =>
          match(r)
            .with({error: P.not(P.nullish).select()}, e => {
              throw new Error(`error loading forecaster stats: ${e.message}`)
            })
            .otherwise(({data}) =>
              safeParseList(
                z.object({
                  question_id: z.string(),
                  profile_id: z.string(),
                  raw_forecast_total: zx.nullish.num,
                  completed: zx.nullish.num,
                  total: zx.nullish.num,
                  completed_on_time: zx.nullish.num,
                  last_forecast_at: zx.nullish.string,
                  participation_status: zx.nullish.bool,
                  question_created_at: z.string(),
                  tasked: zx.nullish.bool
                }),
                data
              )
            )
        )
  })

  const {questions: questionsRaw} = useQuestionsPageQuery({
    userFilter: question_id ? {question_id} : undefined,
    onDbChange: ({id}) => {
      if (callsRaw?.some(p => p.question_id == id)) partsRefetch()
    }
  })
  const questionById = useMemo(() => (questionsRaw ? keyBy(questionsRaw, q => q.id) : undefined), [questionsRaw])
  const questionSelect = useQuestionSelect({
    label: 'filter question',
    items: questionsRaw?.map(d => ({...d, selected: filter.questions.includes(d.id)})) ?? [],
    onChange: (mode, id) =>
      setFilter(
        mode == 'select' ?
          {questions: unique([...filter.questions, id])}
        : {questions: filter.questions.filter(q => q != id)}
      )
  })

  const isAdmin = profile.hasPermission('settings')
  const {
    data: adminCfgs,
    upsert,
    isLoading: adminLoading,
    isDirty
  } = useQuerySbSync({
    table: 'question_admin',
    filter: {workspace_id},
    schema: z.object({
      id: z.string(),
      workspace_id: z.string(),
      created_by: z.string(),
      updated_by: z.string(),
      updated_at: z.string(),
      cfg: z
        .object({
          /** admin config keyed by profile_id */
          forecastAdmin: z.record(zForecastAdminCfg).default({})
        })
        .default({})
    }),
    options: {enabled: isAdmin}
  })

  const getAdminCfg =
    adminCfgs ?
      ({id, profile_id}: {id: string; profile_id: string}) => {
        const cfg = adminCfgs.find(c => c.id == id)?.cfg.forecastAdmin[profile_id]
        return cfg ?? zForecastAdminCfg.parse({})
      }
    : undefined

  const latest = useLatest({getAdminCfg, adminCfgs, upsert})

  const saveAdminCfg = useCallback(
    async ({id, profile_id, cfg: cfgUpdate}: {id: string; profile_id: string; cfg: ForecastAdminCfg}) => {
      const l = latest.current
      if (!l.adminCfgs) return
      const q = l.adminCfgs.find(c => c.id == id) ?? {id: id, cfg: {forecastAdmin: {}}}
      const cfg = {
        ...q.cfg,
        forecastAdmin: {
          ...q.cfg.forecastAdmin,
          [profile_id]: cfgUpdate
        }
      }
      await l
        .upsert({
          id,
          created_by: profile.id,
          updated_by: profile.id,
          updated_at: DateTime.utc().toISO()!,
          workspace_id,
          cfg
        })
        .then(r => {
          match(r)
            .with({error: P.not(P.nullish).select()}, e => showError(e, 'error saving admin notes'))
            .otherwise(() => {})
        })
    },
    []
  )

  const toggleExpanded = (k: PartKeyValue) => {
    const key = partKey(k)
    const keys = query.showForecasterForecasts ?? []
    setFilter({showForecasterForecasts: keys.includes(key) ? keys.filter(m => m != key) : [...keys, key]})
  }

  const [showCols, setShowCols] = useState(8)

  const {profileParts, filterStats, cols, availableCols} = useMemo(() => {
    if (!callsRaw || !questionById) return {byProfile: undefined, filterStats: undefined, cols: undefined}

    const cells = callsRaw
      .map(d => {
        const key = {id: d.question_id, profile_id: d.profile_id}
        const adminCfg = getAdminCfg?.(key)
        const isCompletedInFull = d.total && d.total > 0 ? d.completed == d.total : false
        const question = questionById?.[d.question_id]
        if (!question) {
          console.warn('missing question data for', d.question_id)
          return null
        }
        const filters = {
          tags: isEmpty(tags) || tags.every(t => adminCfg?.tags.includes(t)),
          profiles: isEmpty(filter.profiles) || filter.profiles.includes(d.profile_id),
          status: isEmpty(status) || status.includes(question.cfg.status),
          since: isEmpty(since) || D.fromISO(question.created_at) > D.fromISO(since),
          question: isEmpty(filter.questions) || filter.questions.includes(d.question_id)
        }
        return {
          ...d,
          question,
          completed: d.completed ?? d.raw_forecast_total,
          isCompletedInFull,
          isCompletedOnTime: isCompletedInFull ? d.completed_on_time == d.total : false,
          filters,
          visible: values(filters).every(f => f),
          adminCfg,
          showForecasterForecasts: showForecasterForecasts?.includes(partKey(key)) ?? false,
          toggleForecasts: () => toggleExpanded(key)
        }
      })
      .filter(notNull)

    type Filter = (typeof cells)[number]['filters']
    const cellsFilteredSans = (prop: keyof Filter) =>
      cells.filter(f =>
        entries(f.filters)
          .filter(([k]) => k != prop)
          .every(([_, v]) => v)
      )

    const filterStats = {
      tags: mapValues(
        groupBy(
          cellsFilteredSans('since').flatMap(r => r.adminCfg?.tags.map(tag => ({tag, ...r})) ?? []),
          r => r.tag
        ),
        g => g.length
      ),
      status: mapValues(
        groupBy(cellsFilteredSans('status'), r => r.question.cfg.status),
        g => g.length
      ),
      profiles: mapValues(
        groupBy(cellsFilteredSans('profiles'), r => r.profile_id),
        g => g.length
      )
    }

    const byProfile = groupBy(
      cells.filter(d => d.visible),
      d => d.profile_id
    )

    const questionIds = unique(cells.filter(d => d.visible).map(c => c.question_id))
    const colsAll = sortBy(questionIds, [
      id => pipe(questionById?.[id], q => (q ? questionSortTimestamp(q) : Number.MAX_SAFE_INTEGER)),
      'desc'
    ])
      .map(id => questionById[id])
      .filter(notNull)

    const cols = colsAll.slice(0, showCols)

    const availableCols = colsAll.length

    const profileParts = sortBy(
      mapEntries(byProfile, (profile_id, cellsRaw, i) => {
        const cellByQuestion = keyBy(cellsRaw, c => c.question_id)
        const cells = cols.map(q => cellByQuestion[q.id])
        const taskedOn = cellsRaw.filter(q => q.completed != null && q.total != null)
        const completedInFull = taskedOn.filter(q => q.isCompletedInFull).length
        const completedInFullOnTime = taskedOn.filter(q => q.isCompletedOnTime).length

        return {
          profile_id,
          profile: profilesUI?.[profile_id] ?? ({id: profile_id, name: '(unknown)', roles: []} as ProfileUI),
          cells, //cells: showMore == false ? cells.slice(0, showPerForecaster) :
          taskedOn: taskedOn.length,
          completedInFull,
          completedInFullOnTime,
          completedLate: completedInFull - completedInFullOnTime
        }
      }),
      [p => p.taskedOn, 'desc'],
      [p => p.completedInFull, 'desc']
    )

    return {
      profileParts,
      cols,
      filterStats,
      availableCols
    }
  }, [callsRaw, query, adminCfgs, questionById, showCols])

  return {
    error,
    profileParts,
    cols,
    filterStats,
    setFilter,
    filter,
    saveAdminCfg: isAdmin ? saveAdminCfg : undefined,
    loading: partLoading || adminLoading,
    questionSelect,
    isDirty,
    availableCols,
    setShowCols,
    showCols
  }
}
type UseParticipation = ReturnType<typeof useForecasterParticipation>
type Row = NotNull<UseParticipation['profileParts']>[number]
type Cell = NotNull<Prettify<Row['cells'][number]>>
type CellQuestion = NotNull<Cell['question']>

const PartContext = createContext<{
  mine?: boolean
  saveAdminCfg: UseParticipation['saveAdminCfg']
  question_id?: string
}>(undefined as any)

export const ForecasterParticipation = ({
  mine,
  question_id,
  className
}: {mine?: boolean; question_id?: string} & StyleProps) => {
  const {
    error,
    profileParts,
    setFilter,
    saveAdminCfg,
    filter: {profiles, status, since, tags},
    filterStats,
    loading,
    questionSelect,
    isDirty,
    cols,
    availableCols,
    setShowCols,
    showCols
  } = useForecasterParticipation({mine, question_id})

  const ctx = useMemo(() => ({mine, saveAdminCfg, question_id}), [mine, saveAdminCfg, question_id])

  return (
    <PartContext.Provider value={ctx}>
      <div className={`${className ?? ''} relative`}>
        <SaveStatusCircle className='absolute top-2 right-2' isDirty={isDirty} />
        {ctx.question_id == null && (
          <div className='justify-center flex items-center flex-wrap gap-2 mb-4 my-2'>
            <div className='flex items-center gap-2'>
              <LabelForm>created after</LabelForm>
              <DateInput
                className='w-[9em]'
                valueFormat='D MMM YYYY'
                value={since ?? null}
                onChange={d => setFilter({since: d ?? undefined})}
                clearable
              />
            </div>
            {!mine && (
              <ComboMulti
                value={tags}
                onChange={s => setFilter({tags: s})}
                md={mapValues(adminStatusMd, (v, k) => ({...v, count: filterStats?.tags[k]}))}
                badgeProps={() => ({variant: 'outline'})}
                buttonProps={{__vars: {'--button-padding-x': '0px'}}}
                emptyLabel='any tags'
                hideZeros
                showClear
              />
            )}
            <QuestionStatusFilter
              value={status ?? []}
              onChange={status => setFilter({status})}
              counts={filterStats?.status}
              hideZeros
              showClear
            />
            {!mine && (
              <ProfileFilter
                value={profiles}
                onChange={profiles => setFilter({profiles})}
                counts={filterStats?.profiles}
                emptyLabel='all profiles'
                hideZeros
                showClear
              />
            )}
            {!mine && <QuestionSelect {...questionSelect} />}
          </div>
        )}
        {match({loading, error, profileParts})
          .with({loading: true}, () => (
            <MasonryGrid colWidth='30em' className='gap-8'>
              {range(0, 6).map(i => (
                <PartSkeleton key={i} />
              ))}
            </MasonryGrid>
          ))
          .with({error: P.not(P.nullish).select()}, e => <Alert color='red'>{e.message}</Alert>)
          .with({profileParts: P.not(P.nullish).select()}, profileParts => (
            <Profiles {...{profileParts, cols, availableCols, setShowCols, showCols}} />
          ))
          .otherwise(() => (
            <></>
          ))}
      </div>
    </PartContext.Provider>
  )
}

const PartSkeleton = () => (
  <div className='space-y-2 py-2'>
    <Skeleton width='15em' height='1.8em' radius='md' />
    <Skeleton width='20em' height='1.1em' radius='md' />
    <div className='flex items-center gap-4'>
      <div>
        <Skeleton height={20} circle />
      </div>
      <Skeleton height='1.2em' width='8em' radius='xl' />
      <div>
        <Skeleton height='1.5em' width='1.5em' radius='md' />
      </div>
    </div>
  </div>
)

const Profiles = memo(
  ({
    profileParts,
    cols,
    availableCols,
    setShowCols,
    showCols
  }: Required<Pick<UseParticipation, 'profileParts' | 'cols' | 'setShowCols' | 'availableCols' | 'showCols'>>) => {
    const {question_id} = useContext(PartContext)
    return match(profileParts)
      .with(P.nullish, () => <></>)
      .with([], () => <LabelEmptyList>No matching forecast participation</LabelEmptyList>)
      .with([P._.select()], part => (
        <div className='space-y-8'>
          <ProfileHeading part={part} />
          <MasonryGrid colWidth='20em' className='gap-4'>
            <ProfileQuestions part={part} className='flex flex-col' />
          </MasonryGrid>
        </div>
      ))
      .otherwise(parts => {
        if (cols == null) return <></>

        const gridStyle = {
          gridTemplateColumns: `15em repeat(${cols.length}, 15em) 5em` // Adjust the width as needed
        }

        const styles = {
          rowH: {
            // width: '15em',
            // flex: 'none',
            padding: '0.5em 1em'
          },
          rowCellAlt: {
            backgroundColor: 'var(--mantine-color-gray-1)'
          },
          rowCell: {
            // width: '18em',
            flex: 'none',
            padding: '0.5em 1em',
            borderBottom: '1px solid var(--mantine-color-gray-light)'
          }
        }

        const cellStyle = (i: number) => ({
          ...styles.rowCell,
          ...(i % 2 == 1 ? styles.rowCellAlt : {})
        })

        return question_id ?
            <MasonryGrid colWidth='15em' className='gap-4'>
              {sortBy(parts, p => p.profile.name.toLowerCase()).map((part, i) => (
                <div key={i} className='space-y-4'>
                  <ProfileHeading part={part} />
                  <ProfileQuestions part={part} hideQuestion />
                </div>
              ))}
            </MasonryGrid>
          : <div className='flex flex-col overflow-auto max-h-[calc(100vh-10em)]'>
              <div className='grid  sticky top-0 z-50' style={gridStyle}>
                <div style={styles.rowH} className=''></div>
                {cols.map((q, i) => (
                  <QuestionHeader
                    question={q}
                    style={{backgroundColor: 'white', ...cellStyle(i)}}
                    className='shadow-b'
                    onlyTitle
                  />
                ))}
                <div>
                  {availableCols != null && availableCols > showCols && (
                    <Button
                      leftSection={availableCols ? <HiChevronRight /> : <HiChevronLeft />}
                      onClick={() => setShowCols(showCols + 12)}>
                      {availableCols ? `show more ` : 'show less '}
                    </Button>
                  )}
                </div>
              </div>
              <div className='grid' style={gridStyle}>
                <div style={styles.rowH}></div>
                {cols.map((q, i) => (
                  <QuestionHeader question={q} style={cellStyle(i)} className='' hideTitle />
                ))}
              </div>
              {parts.map((part, i) => (
                <div key={i} className='grid w-max' style={gridStyle}>
                  <ProfileHeading
                    part={part}
                    style={styles.rowH}
                    className='sticky left-0 z-40 bg-white shadow-r py-3 mr-4'
                  />
                  <ProfileQuestions
                    part={part}
                    style={styles.rowCell}
                    altStyle={styles.rowCellAlt}
                    hideQuestion
                    className='px-3 flex items-center justify-center'
                  />
                  <div></div>
                </div>
              ))}
            </div>
      })
  }
)

const ProfileHeading = ({part: p, className, style}: {part: Row} & StyleProps) => {
  const {question_id} = useContext(PartContext)
  // const q = question_id == null ? undefined : p.questions.find(q => q.question_id == question_id)
  return (
    <div style={style} className={`space-y-2 ${className ?? ''}`}>
      <div className='flex items-center gap-2'>
        <SAvatar profile={p.profile} size='sm' />
        <span className='pr-4 font-semibold'>{p.profile.name}</span>
      </div>
      {question_id == null ?
        <div className=''>
          <div>
            <b>{p.taskedOn}</b> tasked questions
          </div>
          {p.taskedOn > 0 && (
            <>
              <div>
                <b>{p.completedInFull}</b> in full, <b>{p.completedInFullOnTime}</b> on time
              </div>
            </>
          )}
        </div>
      : undefined}
    </div>
  )
}

const ProfileQuestions = ({
  part,
  hideQuestion,
  className,
  style,
  altStyle
}: {part: Row; hideQuestion?: boolean; altStyle?: CSSProperties} & StyleProps) => {
  const {mine, saveAdminCfg} = useContext(PartContext)
  return part.cells.map((q, i) => (
    <div key={i} style={{...style, ...(i % 2 == 1 ? altStyle : {})}} className={`${className ?? ''} group`}>
      {q ?
        <Cell
          key={q.question_id}
          questionPart={q}
          save={
            mine || !saveAdminCfg ? undefined : cfg => saveAdminCfg({id: q.question_id, profile_id: q.profile_id, cfg})
          }
          hideQuestion={hideQuestion}
        />
      : <div className='font-bold uppercase opacity-30 group-hover:visible invisible '>no participation</div>}
    </div>
  ))
}

const Cell = ({
  questionPart,
  save,
  ...props
}: {
  questionPart: Cell
  save?: (cfg: ForecastAdminCfg) => void
  hideQuestion?: boolean
}) => {
  const {question_id, completed, total, question, toggleForecasts, showForecasterForecasts, profile_id, ...m} =
    questionPart
  const due = question.cfg.due.forecasting.date
  const {colors} = useSafeMantineTheme()
  const f = useDbForm({stored: questionPart.adminCfg, onUpdate: ({cfg}) => save?.(cfg), debounceSave: 500})
  const {note, tags} = f.values ?? {}
  const [edit, setEdit] = useState(false)
  const ctx = useContext(PartContext)
  const hideQuestion = props.hideQuestion ?? ctx.question_id != null
  return (
    <div key={question_id} className='group w-full space-y-1'>
      {!hideQuestion && <QuestionHeader question={question} />}
      <div className='flex items-center gap-4'>
        <div className='flex flex-col items-center'>
          <ParticipationStatusIcon status={m.participation_status} tasked={m.tasked} />
          {completed != null && <TaskedProgressText progress={{completed, total}} />}
        </div>
        {m.isCompletedInFull && (
          <Chip size='xs' variant='outline' checked={m.isCompletedInFull}>
            in full
          </Chip>
        )}
        {due != null && m.isCompletedOnTime && (
          <Chip size='xs' variant='outline' checked={m.isCompletedOnTime}>
            on time
          </Chip>
        )}

        {due &&
          completed != null &&
          m.completed_on_time != null &&
          m.last_forecast_at != null &&
          pipe(completed - m.completed_on_time, l =>
            !!l ?
              <>
                <span className='text-sm' style={{color: colors.amber?.[6]}}>
                  <b>{l}</b> late, most overdue{' '}
                  <b>
                    {fmtDuration(D.fromISO(m.last_forecast_at!)!.diff(D.fromISO(due!)), {
                      prefix: ''
                    })}
                  </b>
                </span>
              </>
            : undefined
          )}
      </div>
      {save && (!isEmpty(note) || edit) && (
        <RichText
          className='grow'
          value={note ?? ''}
          onChange={n => f.setFieldValue('note', n)}
          readonly={!edit}
          autoFocus
        />
      )}
      <div className={`flex items-center gap-1 min-h-[2.3em] flex-wrap`}>
        {save && tags ?
          edit ?
            <ComboMulti
              value={tags}
              onChange={s => f.setFieldValue('tags', s)}
              md={adminStatusMd}
              badgeProps={() => ({variant: 'outline'})}
              buttonProps={{__vars: {'--button-padding-x': '0px'}}}
              emptyLabel='tags'
              classNames={{
                target: `${
                  isEmpty(tags) && !showForecasterForecasts ?
                    'invisible group-hover:visible data-[expanded=true]:visible'
                  : ''
                }`
              }}
            />
          : <div className='flex gap-2'>
              {tags.map(t => {
                const {label, color} = adminStatusMd[t]
                return (
                  <Badge variant='outline' color={color}>
                    {label}
                  </Badge>
                )
              })}
            </div>

        : <></>}
        <Button
          size='compact-sm'
          leftSection={showForecasterForecasts ? <HiChevronDoubleUp /> : <HiChevronDoubleDown />}
          className={`group-hover:visible ${showForecasterForecasts ? '' : 'invisible'}`}
          onClick={() => toggleForecasts()}>
          forecasts
        </Button>
        {save && (
          <Button
            size='compact-sm'
            leftSection={edit ? <HiCheck /> : <MdEdit />}
            onClick={() => setEdit(!edit)}
            className={`group-hover:visible ${showForecasterForecasts ? '' : 'invisible'}`}>
            {edit ? 'done' : 'edit'}
          </Button>
        )}
      </div>
      {showForecasterForecasts && <Forecasts {...{question_id, profile_id}} />}
    </div>
  )
}

const QuestionHeader = ({
  question,
  hideTitle,
  onlyTitle,
  className,
  style
}: {question: CellQuestion; hideTitle?: boolean; onlyTitle?: boolean} & StyleProps) => {
  const {colors} = useSafeMantineTheme()
  const q = question.cfg
  const due = q.due.forecasting.date
  return (
    <div style={style} className={`space-y-2 ${className ?? ''}`}>
      {!hideTitle && (
        <Link className='font-semibold text-md' href={`/question/${question.id}`}>
          {q.title}
        </Link>
      )}
      {!onlyTitle && (
        <>
          <div className='flex gap-4 items-center flex-wrap'>
            <QuestionTileItems
              statusItem={{md: questionStatusMd[q.status], value: q.status}}
              {...pick(question, ['forecasters_total', 'forecasts_from', 'forecasts_to', 'created_at'])}
            />
            {due ?
              <>
                <DueLabel
                  date={due}
                  color={DateTime.fromISO(due) > DateTime.now() ? colors.green[6] : colors.amber?.[6]}
                />
              </>
            : undefined}
          </div>
          <QuestionAttachmentList attachments={attachmentItems(q.attachments)} />
        </>
      )}
    </div>
  )
}

const Forecasts = ({question_id, profile_id}: {question_id: string; profile_id: string}) => {
  const {data: questionRow} = useQuestionQuery({question_id: question_id})
  const question = questionRow?.ok ? ({id: question_id, ...questionRow.cfg} as QuestionCfg & {id: string}) : undefined
  const {data: subqs} = useSubqQuery({question_id, status: ['approved'], showDraft: false})
  const {getForecasts} = useForecasts({
    filter: {question_id, created_by: profile_id}
  })

  // const questionForecasts = question ? getForecasts({question_id, type: question.forecastType}) : undefined
  const results =
    question ?
      [
        question.disableQuestionForecast ? undefined : (
          {
            type: 'question' as const,
            title: question.title,
            question,
            resolutions: [
              F.getQuestionResolutionForecast({
                question,
                getForecasts
              })
            ]
          }
        ),
        ...(subqs?.map(sq => ({
          type: 'sq' as const,
          title: sq.cfg.title,
          sq,
          resolutions: F.getResolutionForecasts({
            question,
            subq: sq.cfg,
            getForecasts,
            sqId: sq.id
          }).filter(r => r.resolution.enableForecasts)
        })) ?? [])
      ].filter(isNonNullish)
    : undefined

  return (
    <div className='space-y-4'>
      {results == null ?
        range(0, 6).map(i => <Skeleton key={i} width='15em' height='1.8em' radius='md' />)
      : results?.map(({type, resolutions, title}, i) => {
          return (
            <div key={i} className='space-y-2'>
              <b className='text-gray-700'>{title}</b>
              {resolutions.map((r, i) => {
                if (!r) return <></>
                const f = r.forecasts[0]
                return (
                  <div key={i}>
                    {type == 'sq' && resolutions.length > 1 && (
                      <span className='font-semibold text-gray-500'>{r.resolution.label}</span>
                    )}
                    {f ?
                      <>
                        <div className='flex items-center gap-4'>
                          <ForecastStatus status={f.cfg.status} />
                          {r.shouldVizRelative && f.valueRelative != null ?
                            <div className='flex gap-2'>
                              <b>{r.fmt(f.valueRelative, {mode: 'relative'})}</b>
                              <span className='text-gray-500/50'>|</span>
                              <b>{r.fmt(f.value, {mode: 'absolute'})}</b>
                            </div>
                          : <b>{r.fmt(f.value, {mode: 'absolute'})}</b>}
                          <div className=' text-gray-500/70'>{fmtDate(f.created_at, {showTime: true})}</div>
                        </div>
                        {f.cfg.notes && (
                          <Expandable maxHeight='5em' hideScroll>
                            <RichText readonly value={f.cfg.notes} />
                          </Expandable>
                        )}
                      </>
                    : <LabelEmptyList>No forecast</LabelEmptyList>}
                  </div>
                )
              })}
            </div>
          )
        })
      }
    </div>
  )
}

const adminStatusMd = {
  paid: {
    color: 'green',
    label: 'Paid',
    desc: 'Paid in full'
  },
  'paid-partial': {
    color: 'blue',
    label: 'Partial',
    desc: 'Paid partially'
  },
  'pro-bono': {
    color: 'orange',
    label: 'Pro bono',
    desc: 'Forecaster participated untasked'
  },
  bonus: {
    color: 'teal',
    label: 'Bonus',
    desc: 'Bonus'
  },
  'invoice-received': {
    color: 'orange',
    label: 'Invoice received',
    desc: 'Invoice received'
  }
} satisfies Record<AdminStatus, ItemMd>
